"use client";

import { useState } from "react";
import Link from "next/link";
import { SITE_CONFIG } from "@/lib/constants";
import { SITE_TAGLINE } from "@/lib/constants/app.constants";
import Logo from "@/components/vectors/Logo";
import FooterColumn from "@/components/footer/FooterColumn";
import FooterLinkList, {
  type FooterLinkItem,
} from "@/components/footer/FooterLinkList";
import FooterSocial from "@/components/footer/FooterSocial";
import {
  EXPERTISE_ORDER,
  EXPERTISE_LABELS,
  FOOTER_COMPANY_LINKS,
  FOOTER_SOCIAL,
  SERVICE_BUCKETS,
} from "@/lib/config/services.config";
import { shapeAsset } from "@/lib/shapes";

const EXPERTISE_SHAPES: Record<string, string> = {
  strategy: shapeAsset("cube", "coral"),
  design: shapeAsset("pyramid", "pink"),
  development: shapeAsset("rectangle-upright", "blue"),
  prototyping: shapeAsset("arch", "teal"),
  brand: shapeAsset("ball", "yellow"),
  marketing: shapeAsset("cube", "purple"),
  "ai-consulting": shapeAsset("arch", "pink"),
};

export default function Footer() {
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const serviceLinks: readonly FooterLinkItem[] = SERVICE_BUCKETS.map(
    (bucket) => ({
      label: bucket.title,
      href: bucket.href,
      shape: bucket.icon,
    }),
  );
  const expertiseLinks: readonly FooterLinkItem[] = EXPERTISE_ORDER.map(
    (key) => ({
      label: EXPERTISE_LABELS[key],
      href: key === "prototyping" ? "/prototype" : `/services#${key}`,
      shape: EXPERTISE_SHAPES[key],
    }),
  );

  return (
    <footer className="py-16 border-t border-gray-light/10 dark:border-gray-dark/10">
      <div className="container mx-auto px-4">
        <h2 className="sr-only">Site footer</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Logo + Description */}
          <div className="lg:col-span-2 group">
            <Link
              href="/"
              className="text-[#111111] dark:text-white hover:opacity-80 transition-opacity"
              aria-label={`${SITE_CONFIG.name} - Home`}
            >
              <Logo className="h-6 w-auto fill-black dark:fill-white" />
            </Link>

            <p className="mt-4 text-gray-default dark:text-gray-light max-w-md group-hover:text-current transition-colors duration-300">
              {SITE_TAGLINE}
            </p>
          </div>

          <FooterColumn title="Company">
            <FooterLinkList
              items={FOOTER_COMPANY_LINKS}
              hoveredLink={hoveredLink}
              setHoveredLink={setHoveredLink}
            />
            <h3 className="mt-8 mb-6 text-sm font-medium text-dark dark:text-white">
              Services
            </h3>
            <FooterLinkList
              items={serviceLinks}
              hoveredLink={hoveredLink}
              setHoveredLink={setHoveredLink}
            />
          </FooterColumn>

          <FooterColumn title="Expertise">
            <FooterLinkList
              items={expertiseLinks}
              hoveredLink={hoveredLink}
              setHoveredLink={setHoveredLink}
            />
          </FooterColumn>

          <FooterSocial social={FOOTER_SOCIAL} />
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-gray-light/10 dark:border-gray-dark/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-default dark:text-gray-light">
              © {new Date().getFullYear()} Township. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-6 md:justify-end">
              <Link
                href="/privacy"
                className="group text-sm hover:text-gray-light dark:hover:text-gray-dark transition-colors duration-300"
              >
                <span className="relative">
                  Privacy Policy
                  <span className="absolute -bottom-px left-0 w-0 h-px bg-current transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
              <Link
                href="/accessibility"
                className="group text-sm hover:text-gray-light dark:hover:text-gray-dark transition-colors duration-300"
              >
                <span className="relative">
                  Accessibility at Township
                  <span className="absolute -bottom-px left-0 w-0 h-px bg-current transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
